import React from 'react';
import { motion } from 'framer-motion';
import {
  MessageCircle,
  Search,
  Wrench,
  TrendingUp,
  CheckCircle,
  Rocket,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Process.module.css';

const stepIcons = [MessageCircle, Search, Wrench, TrendingUp, CheckCircle, Rocket];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const stepVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.15 },
  },
};

interface ProcessProps {
  data?: {
    tag?: string;
    title: string;
    subtitle: string;
    steps: {
      num?: string;
      title: string;
      subtitle?: string;
      items?: string[];
    }[];
  };
}

export const Process: React.FC<ProcessProps> = ({ data }) => {
  const { t } = useLanguage();

  const defaultSteps = [
    { ...t.process.steps.step1, icon: stepIcons[0] },
    { ...t.process.steps.step2, icon: stepIcons[1] },
    { ...t.process.steps.step3, icon: stepIcons[2] },
    { ...t.process.steps.step4, icon: stepIcons[3] },
    { ...t.process.steps.step5, icon: stepIcons[4] },
    { ...t.process.steps.step6, icon: stepIcons[5] },
  ];

  const content = data || t.process;
  const steps = data
    ? data.steps.map((step, i) => ({
      ...step,
      num: step.num || `0${i + 1}`,
      subtitle: step.subtitle || '',
      items: step.items || [],
      icon: stepIcons[i % stepIcons.length]
    }))
    : defaultSteps;

  return (
    <section className={styles.process} id="process">
      <div className={styles.process__container}>
        {/* Header */}
        <motion.div
          className={styles.process__header}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.15 }}
        >
          {content.tag && <span className={styles.process__tag}><Sparkles size={14} />{content.tag}</span>}
          <h2 className={styles.process__title}>{content.title}</h2>
          <p className={styles.process__subtitle}>{content.subtitle}</p>
        </motion.div>

        {/* Timeline */}
        <motion.div
          className={styles.process__timeline}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isLast = index === steps.length - 1;

            return (
              <motion.div
                key={index}
                className={styles.process__step}
                variants={stepVariants}
              >
                {/* Connector Line */}
                {!isLast && (
                  <div className={styles.process__connector}>
                    <motion.div
                      className={styles.process__connectorLine}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                )}

                {/* Step Number & Icon */}
                <div className={styles.process__stepMarker}>
                  <motion.div
                    className={styles.process__stepNum}
                    whileHover={{ scale: 1.1 }}
                  >
                    <span>{step.num}</span>
                    <div className={styles.process__stepNumGlow} />
                  </motion.div>
                  <div className={styles.process__stepIcon}>
                    <Icon size={20} />
                  </div>
                </div>

                {/* Step Content */}
                <div className={styles.process__stepContent}>
                  <div className={styles.process__stepHeader}>
                    <h3 className={styles.process__stepTitle}>{step.title}</h3>
                    <p className={styles.process__stepSubtitle}>{step.subtitle}</p>
                  </div>

                  <ul className={styles.process__stepList}>
                    {step.items.map((item, itemIndex) => (
                      <motion.li
                        key={itemIndex}
                        className={styles.process__stepItem}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: '-50px' }}
                        transition={{ duration: 0.15 }}
                      >
                        <span className={styles.process__stepItemCheck}>✓</span>
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
